import React, { useState, useEffect } from 'react';
import { DataManager } from '../../services/dataManager';

const ManualQuestionnaireModal = ({ client, onClose, onSave, assessmentType = 'T0', initialData = null }) => {
    const [step, setStep] = useState(initialData ? 3 : 1); // Start at step 3 (Wheel) if editing, otherwise step 1
    const [meetings, setMeetings] = useState([]);
    const [loadingMeetings, setLoadingMeetings] = useState(false);

    // Meta data (date and meeting association)
    const [assessmentDate, setAssessmentDate] = useState(
        initialData?.timestamp ? initialData.timestamp.split('T')[0] : new Date().toISOString().split('T')[0]
    );
    const [selectedMeetingId, setSelectedMeetingId] = useState('');

    // Helper to normalize keys from DB (some might be lowercase legacy)
    const normalizeWheelData = (data) => {
        if (!data) return null;
        const normalized = {};
        const mapping = {
            salute: 'Salute',
            lavoro: 'Lavoro',
            relazioni: 'Relazioni',
            crescita: 'Crescita',
            finanze: 'Finanze',
            ambiente: 'Ambiente',
            emozioni: 'Emozioni',
            famiglia: 'Famiglia',
            divertimento: 'Emozioni' // Map legacy divertimento to emozioni
        };

        Object.entries(data).forEach(([key, val]) => {
            const normalizedKey = mapping[key.toLowerCase()] || key;
            normalized[normalizedKey] = val;
        });
        return normalized;
    };

    // Anamnesis data
    const [anamnesis, setAnamnesis] = useState({
        eta: initialData?.anamnesis?.eta?.[0]?.value || '',
        lavoro: initialData?.anamnesis?.lavoro?.[0]?.value || '',
        quiEdOra: initialData?.anamnesis?.quiEdOra?.[0]?.value || '',
        temiTrattati: initialData?.anamnesis?.temiTrattati?.[0]?.value || '',
        fattoriMedici: initialData?.anamnesis?.fattoriMedici?.[0]?.value || ''
    });

    // Qualitative answers
    const [qualitative, setQualitative] = useState({
        expectations: initialData?.qualitative?.expectations || '',
        resources: initialData?.qualitative?.resources || '',
        suffering: initialData?.qualitative?.suffering || '',
        motivation: initialData?.qualitative?.motivation || ''
    });

    // Wheel of Life scores - normalized
    const [wheelScores, setWheelScores] = useState(normalizeWheelData(initialData?.wheelOfLife) || {
        Lavoro: 5,
        Famiglia: 5,
        Relazioni: 5,
        Salute: 5,
        Emozioni: 5,
        Finanze: 5,
        Crescita: 5,
        Ambiente: 5
    });

    useEffect(() => {
        const loadMeetings = async () => {
            setLoadingMeetings(true);
            try {
                const list = await DataManager.getClientMeetings(client.id, true);
                if (Array.isArray(list)) {
                    // Sort by date ascending (oldest first) as requested
                    const sorted = list.sort((a, b) => new Date(a.date) - new Date(b.date));
                    setMeetings(sorted);
                } else {
                    console.error("DataManager.getClientMeetings did not return an array:", list);
                    setMeetings([]); // Ensure meetings is an array even if API returns unexpected data
                }
            } catch (error) {
                console.error("Error loading meetings:", error);
            } finally {
                setLoadingMeetings(false);
            }
        };
        loadMeetings();
    }, [client.id]);

    // Map areas with custom labels support
    const areas = [
        { key: 'Salute', label: client.wheelLabels?.Salute || client.wheelLabels?.salute || 'Salute Fisica', icon: '💪' },
        { key: 'Lavoro', label: client.wheelLabels?.Lavoro || client.wheelLabels?.lavoro || 'Lavoro/Carriera', icon: '💼' },
        { key: 'Relazioni', label: client.wheelLabels?.Relazioni || client.wheelLabels?.relazioni || 'Relazioni Sociali', icon: '❤️' },
        { key: 'Crescita', label: client.wheelLabels?.Crescita || client.wheelLabels?.crescita || 'Crescita Personale', icon: '🌱' },
        { key: 'Finanze', label: client.wheelLabels?.Finanze || client.wheelLabels?.finanze || 'Finanze', icon: '💰' },
        { key: 'Emozioni', label: client.wheelLabels?.Emozioni || client.wheelLabels?.emozioni || 'Benessere Emotivo', icon: '🎭' },
        { key: 'Ambiente', label: client.wheelLabels?.Ambiente || client.wheelLabels?.ambiente || 'Ambiente/Contesto', icon: '🏡' },
        { key: 'Famiglia', label: client.wheelLabels?.Famiglia || client.wheelLabels?.famiglia || 'Famiglia', icon: '👨‍👩‍👧‍👦' }
    ];

    const handleSave = async () => {
        try {
            if (initialData?.id) {
                await DataManager.deleteBaseline(initialData.id);
            }

            await DataManager.saveBaselineAssessment(
                client.id,
                assessmentType,
                anamnesis,
                qualitative,
                wheelScores,
                null,
                new Date(assessmentDate).toISOString()
            );

            onSave();
            alert('✅ Dati salvati con successo!');
            onClose();
        } catch (error) {
            alert('❌ Errore nel salvataggio: ' + error.message);
        }
    };

    const handleMeetingSelect = (e) => {
        const mId = e.target.value;
        setSelectedMeetingId(mId);
        if (mId) {
            const meeting = meetings.find(m => m.id === mId);
            if (meeting && meeting.date) {
                setAssessmentDate(meeting.date.split('T')[0]);
            }
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in text-slate-800">
            <div className="bg-white rounded-[32px] max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden border border-white/20">
                {/* Header with Step Navigator */}
                <div className="sticky top-0 bg-white border-b border-slate-100 p-6 z-10">
                    <div className="flex justify-between items-start mb-6">
                        <div>
                            <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2">
                                {initialData ? <span>✏️ Modifica Compilazione</span> : <span>📝 Nuova Compilazione</span>}
                            </h2>
                            <p className="text-slate-500 text-sm font-medium mt-1">
                                {client.name} • {assessmentType}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-slate-100 rounded-full text-slate-400 transition"
                        >
                            ✕
                        </button>
                    </div>

                    {/* Step Tabs for direct navigation */}
                    <div className="flex gap-2 p-1 bg-slate-100 rounded-2xl w-fit">
                        {[
                            { id: 1, label: 'Anagrafica', icon: '📋' },
                            { id: 2, label: 'Qualitativo', icon: '💭' },
                            { id: 3, label: 'Ruota Vita', icon: '🎡' }
                        ].map(t => (
                            <button
                                key={t.id}
                                onClick={() => setStep(t.id)}
                                className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 ${step === t.id
                                    ? 'bg-white text-brand-600 shadow-sm'
                                    : 'text-slate-500 hover:bg-white/50'
                                    }`}
                            >
                                <span>{t.icon}</span> {t.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-8">
                    {/* Step 1: Anamnesis */}
                    {step === 1 && (
                        <div className="space-y-6 max-w-2xl mx-auto animate-fade-in">
                            <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100 mb-8 grid md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Data Compilazione</label>
                                    <input
                                        type="date"
                                        className="w-full p-3 bg-white border-2 border-slate-50 rounded-2xl shadow-sm focus:border-brand-200 outline-none transition font-medium"
                                        value={assessmentDate}
                                        onChange={(e) => setAssessmentDate(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Associa a Incontro</label>
                                    <select
                                        className="w-full p-3 bg-white border-2 border-slate-50 rounded-2xl shadow-sm focus:border-brand-200 outline-none transition text-sm font-medium"
                                        value={selectedMeetingId}
                                        onChange={handleMeetingSelect}
                                        disabled={loadingMeetings}
                                    >
                                        <option value="">-- Libera --</option>
                                        {meetings.map(m => (
                                            <option key={m.id} value={m.id}>
                                                {new Date(m.date).toLocaleDateString()} - {m.counselorEmoji} {m.counselorNotes?.substring(0, 15)}...
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">Età</label>
                                    <input
                                        type="number"
                                        className="w-full p-4 bg-slate-50 border-2 border-transparent rounded-[20px] focus:bg-white focus:border-brand-200 outline-none transition font-medium"
                                        value={anamnesis.eta}
                                        onChange={(e) => setAnamnesis({ ...anamnesis, eta: e.target.value })}
                                        placeholder="Es. 35"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">Professione</label>
                                    <input
                                        type="text"
                                        className="w-full p-4 bg-slate-50 border-2 border-transparent rounded-[20px] focus:bg-white focus:border-brand-200 outline-none transition font-medium"
                                        value={anamnesis.lavoro}
                                        onChange={(e) => setAnamnesis({ ...anamnesis, lavoro: e.target.value })}
                                        placeholder="Es. Designer"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">Qui ed Ora</label>
                                    <textarea
                                        className="w-full p-4 bg-slate-50 border-2 border-transparent rounded-[20px] focus:bg-white focus:border-brand-200 outline-none transition font-medium resize-none"
                                        rows="3"
                                        value={anamnesis.quiEdOra}
                                        onChange={(e) => setAnamnesis({ ...anamnesis, quiEdOra: e.target.value })}
                                        placeholder="Stato fenomenologico..."
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end pt-6">
                                <button
                                    onClick={() => setStep(2)}
                                    className="px-12 py-4 bg-slate-900 text-white rounded-2xl font-bold hover:bg-black transition shadow-xl"
                                >
                                    Avanti →
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Qualitative */}
                    {step === 2 && (
                        <div className="space-y-6 max-w-2xl mx-auto animate-fade-in">
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">Aspettative</label>
                                    <textarea
                                        className="w-full p-4 bg-slate-50 border-2 border-transparent rounded-[20px] focus:bg-white focus:border-brand-200 outline-none transition font-medium resize-none"
                                        rows="3"
                                        value={qualitative.expectations}
                                        onChange={(e) => setQualitative({ ...qualitative, expectations: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">Risorse</label>
                                    <textarea
                                        className="w-full p-4 bg-slate-50 border-2 border-transparent rounded-[20px] focus:bg-white focus:border-brand-200 outline-none transition font-medium resize-none"
                                        rows="2"
                                        value={qualitative.resources}
                                        onChange={(e) => setQualitative({ ...qualitative, resources: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">Punti di sofferenza</label>
                                    <textarea
                                        className="w-full p-4 bg-slate-50 border-2 border-transparent rounded-[20px] focus:bg-white focus:border-brand-200 outline-none transition font-medium resize-none"
                                        rows="2"
                                        value={qualitative.suffering}
                                        onChange={(e) => setQualitative({ ...qualitative, suffering: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="flex justify-between pt-6">
                                <button onClick={() => setStep(1)} className="px-6 py-4 text-slate-400 font-bold">Indietro</button>
                                <button
                                    onClick={() => setStep(3)}
                                    className="px-12 py-4 bg-slate-900 text-white rounded-2xl font-bold hover:bg-black transition shadow-xl"
                                >
                                    Avanti →
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Wheel of Life */}
                    {step === 3 && (
                        <div className="space-y-6 max-w-2xl mx-auto animate-fade-in">
                            <div className="grid md:grid-cols-2 gap-4">
                                {areas.map(area => (
                                    <div key={area.key} className="bg-slate-50 p-5 rounded-3xl border border-transparent hover:border-brand-100 transition group hover:bg-white hover:shadow-xl hover:shadow-brand-50/50">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center text-xl group-hover:scale-110 transition">
                                                    {area.icon}
                                                </div>
                                                <span className="font-black text-xs text-slate-700 uppercase tracking-wider">{area.label}</span>
                                            </div>
                                            <span className="text-2xl font-black text-brand-600 font-mono italic">
                                                {wheelScores[area.key] || 0}
                                            </span>
                                        </div>
                                        <input
                                            type="range"
                                            min="0"
                                            max="10"
                                            step="1"
                                            value={wheelScores[area.key] || 0}
                                            onChange={(e) => setWheelScores({ ...wheelScores, [area.key]: parseInt(e.target.value) })}
                                            className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-brand-500"
                                        />
                                    </div>
                                ))}
                            </div>

                            <div className="flex justify-between pt-8 border-t border-slate-100">
                                <button onClick={() => setStep(2)} className="px-6 py-4 text-slate-400 font-bold">Indietro</button>
                                <button
                                    onClick={handleSave}
                                    className="px-12 py-4 bg-brand-600 text-white rounded-2xl font-bold hover:bg-brand-700 transition shadow-2xl shadow-brand-200 transform active:scale-95"
                                >
                                    💾 {initialData ? 'Aggiorna Compilazione' : 'Salva Compilazione'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManualQuestionnaireModal;
